import type { ReactNode } from 'react';

export type TableColumnManagerFieldId = string;

export interface TableColumnManagerField {
  id: TableColumnManagerFieldId;
  label: string;
  icon?: ReactNode;
  mandatory?: boolean;
  pinned?: 'start' | 'end';
}

/** Sanitize column selection against field metadata (mandatory, pinned, unknown ids). */
export function sanitizeTableColumnSelection(
  fields: TableColumnManagerField[],
  rawValue: string[]
): string[] {
  const fieldMap = new Map(fields.map(field => [field.id, field]));
  const uniqueKnown = rawValue.filter(
    (id, index) => fieldMap.has(id) && rawValue.indexOf(id) === index
  );
  const selectedSet = new Set(uniqueKnown);

  const pinnedStart = fields
    .filter(field => field.pinned === 'start')
    .map(field => field.id);
  const pinnedEnd = fields
    .filter(field => field.pinned === 'end')
    .map(field => field.id);

  const middle = uniqueKnown.filter(id => {
    const field = fieldMap.get(id);
    return field && !field.pinned;
  });

  for (const field of fields) {
    if (field.mandatory || field.pinned) {
      selectedSet.add(field.id);
    }
  }

  const selectedMiddle = middle.filter(id => selectedSet.has(id));

  return [...pinnedStart, ...selectedMiddle, ...pinnedEnd];
}

export function setFieldPresent(
  fields: TableColumnManagerField[],
  value: string[],
  fieldId: string,
  present: boolean,
  insertAfterFieldId?: string | null
): string[] {
  const current = sanitizeTableColumnSelection(fields, value);
  if (!present) {
    return sanitizeTableColumnSelection(
      fields,
      current.filter(id => id !== fieldId)
    );
  }
  if (current.includes(fieldId)) {
    return current;
  }

  // Sanitize re-partitions pinned fields, so inserting right after the (middle)
  // anchor in the flat selection preserves the intended middle order.
  const anchorIndex =
    insertAfterFieldId != null ? current.indexOf(insertAfterFieldId) : -1;
  const next = [...current];
  next.splice(anchorIndex >= 0 ? anchorIndex + 1 : next.length, 0, fieldId);
  return sanitizeTableColumnSelection(fields, next);
}

export function reorderValueMiddle(
  fields: TableColumnManagerField[],
  value: string[],
  reorderedMiddleIds: string[]
): string[] {
  const current = sanitizeTableColumnSelection(fields, value);
  const fieldById = new Map(fields.map(field => [field.id, field]));
  const middleMembership = new Set(
    current.filter(id => !fieldById.get(id)?.pinned)
  );

  const pinnedStart = fields
    .filter(field => field.pinned === 'start')
    .map(field => field.id);
  const pinnedEnd = fields
    .filter(field => field.pinned === 'end')
    .map(field => field.id);
  const reorderedSet = new Set(reorderedMiddleIds);
  const middle = reorderedMiddleIds.filter(id => middleMembership.has(id));
  const middleTail = current.filter(id => {
    const field = fieldById.get(id);
    return (
      field &&
      !field.pinned &&
      middleMembership.has(id) &&
      !reorderedSet.has(id)
    );
  });

  return sanitizeTableColumnSelection(fields, [
    ...pinnedStart,
    ...middle,
    ...middleTail,
    ...pinnedEnd
  ]);
}
